import { useMemo } from 'react'
import { useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useDebounce } from '@/hooks'
import { PageLayoutType, PageRead } from '@/api/dashboard'
import { FolderIcon, FolderOutlineIcon, PageContentOutline, PageContentSolid } from '@dashboard/ui'
import { usePagesQuery } from '@/layouts/pages/api/usePagesQuery'
import { HubSettingsMenu, HubSettingsMenuItem, HubSettingsMenuItemSkeleton } from '@/layouts/hub-settings/components'

interface HubSettingsSidebarMenuProps {
  pages: {
    loading: boolean
    primal: PageRead[]
    others: PageRead[]
  }
}

export const HubSettingsSidebarMenu = ({ pages }: HubSettingsSidebarMenuProps) => {
  const { t } = useTranslation()

  const { loading, primal } = pages
  const others = useMemo(
    () =>
      pages.others.toSorted(
        (a: PageRead, b: PageRead) =>
          (a.type === PageLayoutType.Collection && b.type !== PageLayoutType.Collection && 1) ||
          (a.published_at || 0) - (b.published_at || 0) ||
          (a.position || 0) - (b.position || 0) ||
          a.title.localeCompare(b.title),
      ),
    [pages.others],
  )

  return (
    <div className="flex flex-col gap-3 px-3 py-1.5">
      <HubSettingsMenu>
        {(loading && <HubSettingsMenuBones />) ||
          primal.map(page => <PageMenuItem key={page.id} variant="filled" page={page} />)}
      </HubSettingsMenu>

      <HubSettingsMenu label={t('hub-settings.page-settings.custom-pages')}>
        {(loading && <HubSettingsMenuBones />) ||
          others.map(page => <PageMenuItem key={page.id} variant="outline" page={page} />)}
      </HubSettingsMenu>
    </div>
  )
}

function HubSettingsMenuBones() {
  return Array.from({ length: 5 }).map((_, index) => <HubSettingsMenuItemSkeleton key={index} />)
}

type Page = Pick<PageRead, 'id' | 'slug' | 'title' | 'type'>

interface PageMenuItemProps {
  variant?: 'outline' | 'filled'
  prefix?: string
  page: Page
}

function PageMenuItem({ variant, prefix, page }: PageMenuItemProps) {
  switch (page.type) {
    case PageLayoutType.Collection:
      return <CollectionMenuItem variant={variant} prefix={prefix} collection={page} />
    default:
      const icon =
        (variant === 'filled' && PageContentSolid) || (variant === 'outline' && PageContentOutline) || PageContentSolid
      return <HubSettingsMenuItem to={page.id} icon={icon} prefix={prefix} text={page.title} />
  }
}

interface CollectionMenuItemProps {
  variant?: 'outline' | 'filled'
  prefix?: string
  collection: Omit<Page, 'type'>
}

function CollectionMenuItem({ variant = 'filled', prefix, collection }: CollectionMenuItemProps) {
  const { companyId, gameId } = useParams() as { companyId: string; gameId: string }
  const {
    data: pages,
    refetch,
    isLoading,
  } = usePagesQuery(companyId, gameId, { limit: 100, parent_id: collection.id }, { enabled: false })
  const delayedLoading = useDebounce(isLoading, 500)

  const loading = isLoading || delayedLoading
  const icon = (variant === 'filled' && FolderIcon) || (variant === 'outline' && FolderOutlineIcon) || FolderIcon

  return (
    <HubSettingsMenuItem
      loading={loading}
      to={collection.id}
      icon={icon}
      prefix={prefix}
      text={collection.title}
      onUnfold={() => refetch()}
    >
      {!!pages?.length && (
        <HubSettingsMenu>
          {pages.map(page => (
            <PageMenuItem key={page.id} variant={variant} page={page} />
          ))}
        </HubSettingsMenu>
      )}
    </HubSettingsMenuItem>
  )
}
